import type { ContextualSegmentsDrawerState, CustomVariablesFormState } from '@openx/types/targeting/advancedTargeting';
import type { CustomVariablesData, CustomVariablesDataPayload } from '@openx/types/targeting/customVariables';
import { Criteria, type CriteriaValue } from '@openx/types/targeting/targetingValuesTypes';
import isEqual from 'lodash/isEqual';
import { useCallback, useMemo, useState } from 'react';
import {
  getContextualSegmentsDrawerState,
  getContextualSegmentsFromDrawerState,
  hasIncompleteContextualInputs,
  isContextualUpdatedCheck,
} from '../helper';

type CustomVariablesValue = { custom?: CustomVariablesDataPayload[] | null; op?: CriteriaValue } | null | undefined;

/** Drawer editing state of the custom variables field. */
export const useCustomVariablesDrawerState = (value: CustomVariablesValue) => {
  const initState = useMemo<CustomVariablesFormState>(
    () => ({ custom: value?.custom ?? [], op: value?.op ?? Criteria.ALL }),
    [value]
  );
  const [params, setParams] = useState<CustomVariablesFormState>(initState);

  const restore = useCallback(() => setParams(initState), [initState]);
  const onEnter = useCallback(
    ({ val }: CustomVariablesData) => setParams(prevParams => ({ ...prevParams, custom: val })),
    []
  );

  return {
    initState,
    params,
    setParams,
    restore,
    onEnter,
    isUpdated: !isEqual(initState, params),
    /** Value to store in the form field; an empty list clears it. */
    fieldValue: params.custom.length ? params : null,
  };
};

/**
 * Drawer editing state of the contextual segments field (V1, or V2 when `segmentsV2`). V2 edits the provider of the
 * account (`accountProviderId`).
 */
export const useContextualSegmentsDrawerState = (
  value: CustomVariablesDataPayload[] | null | undefined,
  segmentsV2: boolean,
  accountProviderId?: string
) => {
  const initState = useMemo(
    () => getContextualSegmentsDrawerState(value, segmentsV2, accountProviderId),
    [value, segmentsV2, accountProviderId]
  );
  const [params, setParams] = useState<ContextualSegmentsDrawerState>(initState);

  const restore = useCallback(() => setParams(initState), [initState]);
  const onAdd = useCallback(
    (contextual: CustomVariablesDataPayload[]) => setParams(prevParams => ({ ...prevParams, contextual })),
    []
  );

  return {
    initState,
    params,
    setParams,
    restore,
    onAdd,
    isUpdated: isContextualUpdatedCheck(initState, params, segmentsV2),
    /** Some edits cannot be saved yet (V1 provider without segment ids), so nothing should be applied. */
    isIncomplete: hasIncompleteContextualInputs(params, segmentsV2),
    /** Value to store in the form field. */
    fieldValue: getContextualSegmentsFromDrawerState(params, segmentsV2, accountProviderId),
  };
};
